import { useCallback, useRef, useState } from 'react'
import './linkwatch.css'

type Bucket = 'good' | 'redirected' | 'errors'

interface LinkResult {
  url: string
  text: string
  status: number | null
  error: string | null
  bucket: Bucket
}

const BUCKET_META: Record<Bucket, { label: string; note: string }> = {
  good: { label: '200 · Good', note: '2xx — resolved fine' },
  redirected: { label: '300 · Redirected', note: '3xx — points somewhere else' },
  errors: { label: '400/500 · Errors', note: '4xx, 5xx, or unreachable' },
}

function StatusPill({ status }: { status: number | null }) {
  return <span className="linkwatch-pill">{status ?? 'ERR'}</span>
}

export function LinkwatchPage() {
  const [url, setUrl] = useState('')
  const [running, setRunning] = useState(false)
  const [started, setStarted] = useState(false)
  const [total, setTotal] = useState(0)
  const [log, setLog] = useState<LinkResult[]>([])
  const [buckets, setBuckets] = useState<Record<Bucket, LinkResult[]>>({
    good: [],
    redirected: [],
    errors: [],
  })
  const [fatalError, setFatalError] = useState('')
  const esRef = useRef<EventSource | null>(null)

  const reset = () => {
    setStarted(false)
    setTotal(0)
    setLog([])
    setBuckets({ good: [], redirected: [], errors: [] })
    setFatalError('')
  }

  const runCrawl = useCallback((targetUrl: string) => {
    reset()
    setRunning(true)
    esRef.current?.close()

    // Absolute path: this page is served from the app's own domain root,
    // so /api/crawl always points at the Java backend regardless of which
    // client-side route the page was reached from.
    const es = new EventSource(`/api/crawl?url=${encodeURIComponent(targetUrl)}`)
    esRef.current = es

    es.addEventListener('start', (e) => {
      const data = JSON.parse((e as MessageEvent).data)
      setStarted(true)
      setTotal(data.total)
      if (data.total === 0) setRunning(false)
    })

    es.addEventListener('link', (e) => {
      const data: LinkResult = JSON.parse((e as MessageEvent).data)
      setLog((prev) => {
        const next = [...prev, data]
        return next.length > 400 ? next.slice(-400) : next
      })
      setBuckets((prev) => ({
        ...prev,
        [data.bucket]: [...prev[data.bucket], data],
      }))
    })

    es.addEventListener('done', () => {
      setRunning(false)
      es.close()
    })

    es.addEventListener('fatal', (e) => {
      const data = JSON.parse((e as MessageEvent).data)
      setFatalError(data.message || 'Something went wrong.')
      setRunning(false)
      es.close()
    })

    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) return
      setRunning(false)
      es.close()
    }
  }, [])

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!url.trim() || running) return
    runCrawl(url.trim())
  }

  const checked = log.length
  const good = buckets.good.length
  const redirected = buckets.redirected.length
  const errors = buckets.errors.length

  return (
    <section className="section linkwatch">
      <div className="container">
        <div className="section-heading">
          <span className="label">Internal Tool</span>
          <h1>
            Check every link on a page.<br />
            <span className="gradient-text">Before your visitors do.</span>
          </h1>
          <p>
            Paste a URL. LinkWatch pulls every &lt;a href&gt; it can find, checks each
            one's response, and sorts the results into good, redirected, and broken.
          </p>
        </div>

        <form className="linkwatch-prompt" onSubmit={onSubmit}>
          <input
            type="text"
            className="linkwatch-prompt__input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            disabled={running}
            autoFocus
          />
          <button type="submit" className="btn btn--primary" disabled={running}>
            {running ? 'Crawling…' : 'Run Check'}
          </button>
        </form>

        {fatalError && <div className="linkwatch-error">{fatalError}</div>}

        {started && (
          <div className="linkwatch-status">
            <span>
              {running
                ? `Checking ${checked}/${total} links…`
                : `Done — ${checked}/${total} links checked`}
            </span>
            <span className="linkwatch-status__counts">
              <span className="linkwatch-status__count linkwatch-status__count--good">
                Good <b>{good}</b>
              </span>
              <span className="linkwatch-status__count linkwatch-status__count--redirected">
                Redirected <b>{redirected}</b>
              </span>
              <span className="linkwatch-status__count linkwatch-status__count--errors">
                Errors <b>{errors}</b>
              </span>
            </span>
          </div>
        )}

        {running && (
          <div className="linkwatch-log">
            {log.slice(-60).map((item, i) => (
              <div className={`linkwatch-log__row linkwatch-log__row--${item.bucket}`} key={i}>
                <StatusPill status={item.status} />
                <span className="linkwatch-log__url">{item.url}</span>
              </div>
            ))}
            <div className="linkwatch-log__cursor">
              <span>watching</span>
              <span className="linkwatch-log__blink" />
            </div>
          </div>
        )}

        {started && !running && (
          <div className="linkwatch-buckets">
            {(['good', 'redirected', 'errors'] as Bucket[]).map((key) => (
              <div className={`linkwatch-bucket linkwatch-bucket--${key}`} key={key}>
                <div className="linkwatch-bucket__head">
                  <span>{BUCKET_META[key].label}</span>
                  <span className="linkwatch-bucket__n">{buckets[key].length}</span>
                </div>
                <div className="linkwatch-bucket__sub">{BUCKET_META[key].note}</div>
                {buckets[key].length === 0 ? (
                  <div className="linkwatch-bucket__empty">Nothing here</div>
                ) : (
                  <ul className="linkwatch-bucket__list">
                    {buckets[key].map((item, i) => (
                      <li key={i}>
                        <StatusPill status={item.status} />
                        <a
                          className="linkwatch-bucket__link"
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {item.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}